// React Imports
import { useState, useEffect } from "react";

// Toolkit Imports
import { themeClasses as T } from "sj-ui-toolkit/src/atlas/utils";
import { useBreadcrumb } from "sj-ui-toolkit/src/atlas/hooks/useBreadcrumbs";
import { useHideTimeSpent } from "sj-ui-toolkit/src/integrations";

// Local Imports
import S from "./HistoryTab.module.scss";
import { languageDisplayNames } from "sj-ui-toolkit/src/constants/content";

// External Libraries
import { Accordion, AccordionTab } from "primereact/accordion";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Skeleton } from "primereact/skeleton";

// Hooks & Helpers
import { fetchUserActivities, fetchFinishedTournaments } from "sj-ui-toolkit/src/api";
import type { UserActivity } from "sj-ui-toolkit/src/api/userActivities";
import type { TournamentDetails } from "sj-ui-toolkit/src/api/types/tournamentTypes";
import type { UserExam } from "sj-ui-toolkit/src/client/svcProctor";
import {
  formatShortDate,
  formatTimeToComplete,
  getCurrentSelectedTenant,
} from "sj-ui-toolkit/src/utils";
import { getProctorClient, getTaxonomyClient } from "sj-ui-toolkit/src/client/svcProctor";
import { getChoresClient } from "sj-ui-toolkit/src/client";
import type { KnowledgeAssessment } from "sj-ui-toolkit/src/client/svcChores";
import { useApiPagination } from "sj-ui-toolkit/src/hooks";
import { ROWS_PER_PAGE_OPTIONS } from "sj-ui-toolkit/src/constants";
import type { SortOrder, DataTableFilterMeta } from "primereact/datatable";
import type { ColumnSortEvent } from "primereact/column";

// The generated proctor type derives UserExam from proto schemas; these runtime
// fields exist in the API response but may not be reflected in the generated
// TypeScript types yet.
type UserExamRuntime = UserExam & {
  scorePercent?: number | null;
  submitTime?: string | null;
  examInstanceId?: string | null;
};

interface CompletedAssessment {
  name: string;
  href: string | null;
  type: string;
  score: string;
  language: string;
  dateCompleted: string | null | undefined;
}

// Note: Columns below are intentionally commented out and will be excluded from the initial release
// They depend on data that is not yet available or easily accessible
// Shrpa-2485/2486
export function HistoryTab() {
  useBreadcrumb({ label: "History", route: "/profile/history", display: "page" });
  const hideTimeSpent = useHideTimeSpent();
  const [tournamentsLoading, setTournamentsLoading] = useState(true);
  const [assessmentsLoading, setAssessmentsLoading] = useState(true);
  const [finishedTournaments, setFinishedTournaments] = useState<TournamentDetails[]>([]);
  const [completedAssessments, setCompletedAssessments] = useState<CompletedAssessment[]>([]);
  const skeletonItems = Array(10).fill({}) as UserActivity[];

  const {
    data: userHistory,
    loading,
    totalRecords,
    lazyState,
    onPage,
    onSort,
    onFilter,
  } = useApiPagination({
    loadData: async ({ page, perPage }) => {
      const response = await fetchUserActivities({ page, perPage });
      return response || { list: [], pagesNumber: 0 };
    },
    calculateTotalRecords: (response, pageSize) => {
      const dataArray = response?.list || [];
      const totalPages = response?.pagesNumber || 1;
      if (totalPages === 1) {
        return dataArray.length;
      }
      return (totalPages - 1) * pageSize + dataArray.length;
    },
    storageKey: "userHistoryTable",
    dataKey: "list",
  });

  useEffect(() => {
    const fetchTournamentsData = async () => {
      try {
        const tournaments = await fetchFinishedTournaments();
        setFinishedTournaments(tournaments);
      } catch (error) {
        console.error("Error fetching finished tournaments:", error);
      } finally {
        setTournamentsLoading(false);
      }
    };

    const fetchAssessmentsData = async () => {
      try {
        const [proctorResult, choresResult, sectionsResult] = await Promise.all([
          getProctorClient().fetchUserExams({
            includeStatuses: ["completed"],
            excludeAssignments: true,
          }),
          getChoresClient().myChores(),
          getTaxonomyClient().listSections(undefined, getCurrentSelectedTenant() ?? ""),
        ]);

        if (proctorResult.error) throw proctorResult.error;

        const sectionsByPath = Object.fromEntries(
          (sectionsResult.data ?? []).map(s => [s.path, s.title])
        );

        const exams = (proctorResult.data?.exams ?? []) as UserExamRuntime[];
        const userInitiated: CompletedAssessment[] = exams.map(exam => {
          const prefixes = exam.examConfig?.filters?.topicPrefixes ?? [];
          const sectionTitles = prefixes.map(p => sectionsByPath[p]).filter(Boolean);
          return {
            name: sectionTitles.length > 0 ? sectionTitles.join(", ") : (exam.examKey ?? ""),
            href: exam.examInstanceId ? `/ka/result/${exam.examInstanceId}` : null,
            type: "Self-initiated",
            score: exam.scorePercent != null ? `${Math.round(exam.scorePercent)}%` : "-",
            language: exam.examConfig?.parameters?.programmingLanguage || "-",
            dateCompleted: exam.submitTime,
          };
        });

        const choresItems = choresResult.data?.items ?? [];
        const assigned: CompletedAssessment[] = choresItems
          .filter(
            item =>
              "knowledgeAssessment" in item.content &&
              item.content.knowledgeAssessment.status.progress === "Completed"
          )
          .map(item => {
            const ka = (item.content as { knowledgeAssessment: KnowledgeAssessment })
              .knowledgeAssessment;
            const s = ka.status.score;
            return {
              name: item.listing.title,
              href: ka.status.examInstanceId ? `/ka/result/${ka.status.examInstanceId}` : null,
              type: "Assigned",
              score: s && s.possible > 0 ? `${Math.round((s.earned / s.possible) * 100)}%` : "-",
              language: String(ka.configuration?.parameters?.programmingLanguage || "-"),
              dateCompleted: ka.status.completedAt,
            };
          });

        setCompletedAssessments([...userInitiated, ...assigned]);
      } catch (error) {
        console.error("Error fetching completed assessments:", error);
      } finally {
        setAssessmentsLoading(false);
      }
    };

    fetchTournamentsData();
    fetchAssessmentsData();
  }, []);

  const formatPoints = (points: number | string): string => {
    const num = Number(points);
    if (isNaN(num)) return String(points);
    return `${num >= 0 ? "+ " : "- "}${Math.abs(num)} points`;
  };

  const languageTemplate = (language: string): string => {
    return languageDisplayNames[language] || language;
  };

  // Sort function that places "-" at the end, regardless of sort direction
  const languageSortFunction = (event: ColumnSortEvent): CompletedAssessment[] => {
    const data = event.data as CompletedAssessment[];
    const order = event.order ?? 1;
    return [...data].sort((a, b) => {
      if (a.language === "-" && b.language === "-") return 0;
      if (a.language === "-") return 1;
      if (b.language === "-") return -1;
      return order * a.language.localeCompare(b.language);
    });
  };

  return (
    <div className={S.root} data-testid="history-tab">
      <Accordion multiple className={T.bgElevation1} aria-label="Activity history">
        <AccordionTab
          header={
            <div className={S.tabHeader}>
              <span className="atlas-h5">Lesson Activity</span>
              <span className="body-sm">
                A record of all you have accomplished on the Security Journey platform.
              </span>
            </div>
          }
          pt={{ headerAction: { "data-testid": "history-lesson-activity-accordion-tab" } }}
        >
          <div className={S.tabContent}>
            <DataTable
              aria-label="Lesson activity history"
              value={loading ? skeletonItems : (userHistory as UserActivity[])}
              lazy
              paginator
              first={lazyState.first}
              rows={lazyState.rows}
              totalRecords={totalRecords}
              onPage={onPage}
              onSort={onSort}
              onFilter={onFilter}
              sortField={lazyState.sortField ?? undefined}
              sortOrder={lazyState.sortOrder as SortOrder}
              filters={lazyState.filters as DataTableFilterMeta | undefined}
              data-testid="history-lesson-activity-table"
              pt={{
                paginator: {
                  RPPDropdown: { select: { "aria-label": "Rows per page" } },
                  root: { className: "mt-[12px]" },
                },
              }}
              rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
              emptyMessage="No lesson activity found."
            >
              <Column
                sortable
                field="name"
                header="Lesson name"
                pt={{
                  headerCell: { className: "w-2/5" },
                  bodyCell: { className: "w-2/5 truncate" },
                }}
                body={(rowData: UserActivity) => (loading ? <Skeleton /> : rowData.name)}
              />
              <Column
                sortable
                field="points"
                header="Points"
                body={(rowData: UserActivity) =>
                  loading ? <Skeleton /> : formatPoints(rowData.points)
                }
              />
              {!hideTimeSpent && (
                <Column
                  sortable
                  field="timeSpent"
                  header="Time to complete"
                  body={(rowData: UserActivity) =>
                    loading ? <Skeleton /> : formatTimeToComplete(rowData.timeSpent)
                  }
                />
              )}
              <Column
                sortable
                field="date"
                header="Date completed"
                body={(rowData: UserActivity) =>
                  loading ? <Skeleton /> : formatShortDate(rowData.date)
                }
              />
            </DataTable>
          </div>
        </AccordionTab>

        <AccordionTab
          header={
            <div className={S.tabHeader}>
              <span className="atlas-h5">Finished Tournaments</span>
              <span className="body-sm">Tournaments you have completed.</span>
            </div>
          }
          pt={{ headerAction: { "data-testid": "history-tournaments-accordion-tab" } }}
        >
          <div className={S.tabContent}>
            <DataTable
              aria-label="Finished tournaments"
              value={
                tournamentsLoading
                  ? (Array(10).fill({}) as TournamentDetails[])
                  : finishedTournaments
              }
              paginator={!tournamentsLoading}
              rows={10}
              data-testid="history-tournaments-table"
              pt={{
                paginator: {
                  RPPDropdown: { select: { "aria-label": "Rows per page" } },
                  root: { className: "mt-[12px]" },
                },
              }}
              rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
              emptyMessage="No tournaments completed."
            >
              <Column
                sortable
                field="name"
                header="Tournament name"
                pt={{
                  headerCell: { className: "w-2/5" },
                  bodyCell: { className: "w-2/5 truncate" },
                }}
                body={(rowData: TournamentDetails) =>
                  tournamentsLoading ? <Skeleton /> : rowData.name
                }
              />
              <Column
                sortable
                field="tournamentUserPoints"
                header="Points"
                body={(rowData: TournamentDetails) =>
                  tournamentsLoading ? <Skeleton /> : formatPoints(rowData.tournamentUserPoints)
                }
              />
              <Column
                sortable
                field="completedExercises"
                header="Exercises completed"
                body={(rowData: TournamentDetails) =>
                  tournamentsLoading ? (
                    <Skeleton />
                  ) : (
                    `${rowData.completedExercises} / ${rowData.exercisesCount}`
                  )
                }
              />
              <Column
                sortable
                field="finishDate"
                header="Finish date"
                body={(rowData: TournamentDetails) =>
                  tournamentsLoading ? <Skeleton /> : formatShortDate(rowData.finishDate)
                }
              />
            </DataTable>
          </div>
        </AccordionTab>

        <AccordionTab
          header={
            <div className={S.tabHeader}>
              <span className="atlas-h5">Completed Knowledge Assessments</span>
              <span className="body-sm">Knowledge assessments you have completed.</span>
            </div>
          }
          pt={{ headerAction: { "data-testid": "history-assessments-accordion-tab" } }}
        >
          <div className={S.tabContent}>
            <DataTable
              aria-label="Completed assessments"
              className={T.bgElevation1}
              value={
                assessmentsLoading
                  ? (Array(10).fill({}) as CompletedAssessment[])
                  : completedAssessments
              }
              paginator={!assessmentsLoading}
              rows={10}
              data-testid="history-assessments-table"
              pt={{
                paginator: {
                  RPPDropdown: { select: { "aria-label": "Rows per page" } },
                  root: { className: "mt-[12px]" },
                },
              }}
              rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
              emptyMessage="No assessments completed."
            >
              <Column
                sortable
                field="name"
                header="Assessment name"
                pt={{
                  headerCell: { className: "w-2/5" },
                  bodyCell: { className: "w-2/5 truncate" },
                }}
                body={(rowData: CompletedAssessment) =>
                  assessmentsLoading ? (
                    <Skeleton />
                  ) : rowData.href ? (
                    <a href={rowData.href} className="underline">
                      {rowData.name}
                    </a>
                  ) : (
                    rowData.name
                  )
                }
              />
              <Column
                sortable
                field="type"
                header="Type"
                body={(rowData: CompletedAssessment) =>
                  assessmentsLoading ? <Skeleton /> : rowData.type
                }
              />
              <Column
                sortable
                field="score"
                header="Score"
                body={(rowData: CompletedAssessment) =>
                  assessmentsLoading ? <Skeleton /> : rowData.score
                }
              />
              <Column
                sortable
                sortFunction={languageSortFunction}
                field="language"
                header="Language"
                body={(rowData: CompletedAssessment) =>
                  assessmentsLoading ? <Skeleton /> : languageTemplate(rowData.language)
                }
              />
              <Column
                sortable
                field="dateCompleted"
                header="Date completed"
                body={(rowData: CompletedAssessment) =>
                  assessmentsLoading ? <Skeleton /> : formatShortDate(rowData.dateCompleted ?? "")
                }
              />
            </DataTable>
          </div>
        </AccordionTab>
      </Accordion>
    </div>
  );
}
